import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { useGetUserSession } from './auth'

export const EU_COMMISSION_NEWS_RSS_SOURCE = 'EU_COMMISSION_NEWS_RSS'

export const REGULATORY_NEWS_QUERY_ROOT = 'regulatoryNews'

export const NEWSROOM_PATH = '/newsroom'

/** Must match server `LEX_REGULATORY_NEWS_TOPIC_DOMAIN`. */
export const LEX_REGULATORY_NEWS_TOPIC_DOMAIN = 'lex://regulatory-news/topic/v1'

export type RegulatoryNewsCategory = {
  domain: string | null
  label: string
}

export type RegulatoryNewsSubscription = RegulatoryNewsCategory & {
  source: string
}

export type OrganizationAlert = {
  id: string
  source: string
  externalId: string
  derivedAt: string
  matchedCategories: RegulatoryNewsCategory[]
  publishedAt: string | null
  title: string | null
  link: string | null
  descriptionText: string | null
  imageUrl: string | null
  readAt: string | null
  dismissedAt: string | null
}

export type OrganizationAlertsListResponse = {
  items: OrganizationAlert[]
  pageInfo: {
    nextCursor: string | null
  }
}

export type OrganizationAlertsUnreadCountResponse = {
  count: number
}

export type OrganizationAlertArticleResponse = {
  title: string | null
  body: string
  sourceUrl: string
  wasTranslated: boolean
  workflowRunId: string
}

export function regulatoryNewsCategoryKey(category: RegulatoryNewsCategory): string {
  return `${category.domain ?? ''}\0${category.label}`
}

export function isEuLexRegulatoryNewsSubscription(
  subscription: RegulatoryNewsSubscription
): boolean {
  return (
    subscription.source === EU_COMMISSION_NEWS_RSS_SOURCE &&
    subscription.domain === LEX_REGULATORY_NEWS_TOPIC_DOMAIN
  )
}

/** DOM-safe id for topic checkboxes (keys use a NUL delimiter). */
export function regulatoryNewsTopicCheckboxId(
  category: RegulatoryNewsCategory
): string {
  const key = regulatoryNewsCategoryKey(category)
  return `regulatory-topic-${key.split('\0').join('--')}`
}

export function formatUnreadBadgeCount(count: number): string | undefined {
  if (count <= 0) {
    return undefined
  }
  if (count > 99) {
    return '99+'
  }
  return String(count)
}

const UNREAD_COUNT_REFETCH_INTERVAL_MS = 60 * 1000

export function useRegulatoryNewsTopics(
  source: string = EU_COMMISSION_NEWS_RSS_SOURCE
) {
  return useQuery<RegulatoryNewsCategory[]>({
    queryKey: [REGULATORY_NEWS_QUERY_ROOT, 'topics', source],
    queryFn: async () => {
      const response = await apiClient.get<RegulatoryNewsCategory[]>(
        '/regulatory-news/topics',
        { params: { source } }
      )
      return response.data
    },
  })
}

export function useRegulatoryNewsSubscriptions(organizationId: string | undefined) {
  return useQuery<RegulatoryNewsSubscription[]>({
    queryKey: [REGULATORY_NEWS_QUERY_ROOT, 'subscriptions', organizationId],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }
      const response = await apiClient.get<RegulatoryNewsSubscription[]>(
        `/organizations/${organizationId}/regulatory-news/subscriptions`
      )
      return response.data
    },
    enabled: Boolean(organizationId),
  })
}

export function useReplaceRegulatoryNewsSubscriptions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      organizationId,
      source,
      categories,
    }: {
      organizationId: string
      source: string
      categories: RegulatoryNewsCategory[]
    }) => {
      const response = await apiClient.put<RegulatoryNewsSubscription[]>(
        `/organizations/${organizationId}/regulatory-news/subscriptions`,
        { source, categories }
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          REGULATORY_NEWS_QUERY_ROOT,
          'subscriptions',
          variables.organizationId,
        ],
      })
      void queryClient.invalidateQueries({
        queryKey: [REGULATORY_NEWS_QUERY_ROOT, 'alerts', variables.organizationId],
      })
      void queryClient.invalidateQueries({
        queryKey: [
          REGULATORY_NEWS_QUERY_ROOT,
          'unreadCount',
          variables.organizationId,
        ],
      })
    },
  })
}

export function useOrganizationAlertsInfinite(options: {
  organizationId: string | undefined
  includeDismissed?: boolean
  limit?: number
}) {
  const { organizationId, includeDismissed = false, limit = 20 } = options

  return useInfiniteQuery<OrganizationAlertsListResponse, Error>({
    queryKey: [
      REGULATORY_NEWS_QUERY_ROOT,
      'alerts',
      organizationId,
      { includeDismissed, limit },
    ],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }
      const response = await apiClient.get<OrganizationAlertsListResponse>(
        `/organizations/${organizationId}/alerts`,
        {
          params: {
            cursor: pageParam ?? undefined,
            limit,
            includeDismissed: includeDismissed ? 'true' : 'false',
          },
        }
      )
      return response.data
    },
    getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor,
    enabled: Boolean(organizationId),
  })
}

export function useOrganizationAlertsUnreadCount(
  organizationId: string | undefined
) {
  const { data: session } = useGetUserSession()

  return useQuery<OrganizationAlertsUnreadCountResponse, Error>({
    queryKey: [REGULATORY_NEWS_QUERY_ROOT, 'unreadCount', organizationId],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }
      const response = await apiClient.get<OrganizationAlertsUnreadCountResponse>(
        `/organizations/${organizationId}/alerts/unread-count`
      )
      return response.data
    },
    enabled: Boolean(
      organizationId && session?.organizationId && session.isArtemAdmin
    ),
    refetchInterval: UNREAD_COUNT_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  })
}

function invalidateAlertQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string
) {
  void queryClient.invalidateQueries({
    queryKey: [REGULATORY_NEWS_QUERY_ROOT, 'alerts', organizationId],
  })
  void queryClient.invalidateQueries({
    queryKey: [REGULATORY_NEWS_QUERY_ROOT, 'unreadCount', organizationId],
  })
}

export function useMarkOrganizationAlertRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      organizationId,
      alertId,
    }: {
      organizationId: string
      alertId: string
    }) => {
      await apiClient.post(
        `/organizations/${organizationId}/alerts/${alertId}/read`
      )
    },
    onSuccess: (_data, variables) => {
      invalidateAlertQueries(queryClient, variables.organizationId)
    },
  })
}

export function useOrganizationAlertArticle(
  organizationId: string | undefined,
  alertId: string | null,
  options?: { enabled?: boolean }
) {
  const enabled =
    Boolean(organizationId) && Boolean(alertId) && (options?.enabled ?? true)

  return useQuery<OrganizationAlertArticleResponse, Error>({
    queryKey: [
      REGULATORY_NEWS_QUERY_ROOT,
      'article',
      organizationId,
      alertId,
    ],
    enabled,
    queryFn: async () => {
      const response = await apiClient.get<OrganizationAlertArticleResponse>(
        `/organizations/${organizationId}/alerts/${alertId}/article`
      )
      return response.data
    },
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })
}

export function useDismissOrganizationAlert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      organizationId,
      alertId,
    }: {
      organizationId: string
      alertId: string
    }) => {
      await apiClient.post(
        `/organizations/${organizationId}/alerts/${alertId}/dismiss`
      )
    },
    onSuccess: (_data, variables) => {
      invalidateAlertQueries(queryClient, variables.organizationId)
    },
  })
}
